import { canSendToGTM } from "./analiticsConfigByFacility";
import { ConfirmationData } from "../../app/models/ConfirmationData";
import { AddToCartEvent } from "../../app/models/AddToCartEvent";
import { ViewCartEvent } from "../../app/models/ViewCartEvent";
import { PurchaseItem } from "../../app/models/PurchaseItem";
import { PurchaseEvent } from "../../app/models/PurchaseEvent";
import { BeginCheckoutEvent } from "../../app/models/BeginCheckoutEvent";
import { BeginCheckoutItem } from "../../app/models/BeginCheckoutItem";
import { format } from "date-fns";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Wysyła event add_to_cart do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane produktu dodanego do koszyka
 */
export const sendAddToCartEvent = (facilityId: string, data: AddToCartEvent): void => {
  if (canSendToGTM(facilityId)) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: data,
    });
    console.info("GTM event sent: add_to_cart", data);
  }
};

/**
 * Wysyła event view_cart do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane koszyka
 */
export const sendViewCartEvent = (facilityId: string, data: ViewCartEvent): void => {
  if (canSendToGTM(facilityId)) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_cart",
      ecommerce: data,
    });
    console.info("GTM event sent: view_cart", data);
  }
};

/**
 * Wysyła event begin_checkout do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane rozpoczęcia procesu checkout
 */
export const sendBeginCheckoutEvent = (facilityId: string, data: BeginCheckoutEvent): void => {
  if (canSendToGTM(facilityId)) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "begin_checkout",
      ecommerce: data,
    });
    console.info("GTM event sent: begin_checkout", data);
  }
};

/**
 * Wysyła event purchase do GTM
 * @param facilityId - ID obiektu
 * @param data - Dane zakończonego zakupu
 */
export const sendPurchaseEvent = (facilityId: string, data: PurchaseEvent): void => {
  if (canSendToGTM(facilityId)) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      ecommerce: data,
    });
    console.info("GTM event sent: purchase", data);
  }
};

/**
 * Pomocnicza funkcja do konwersji danych koszyka na format dla GTM
 * @param basketItem - Element koszyka
 * @returns Dane w formacie GTM
 */
export const convertBasketItemToGtmFormat = (basketItem: {
  assortIds: string;
  assortName?: string;
  qnt: number;
  price?: number;
  facilityIds: string;
}): AddToCartEvent => {
  // Wyciągamy faktyczne ID asortymentu (usuwamy prefiks "0,+")
  const itemId = basketItem.assortIds.replace(/^0,\+/, "");

  return {
    currency: "PLN",
    value: basketItem.price ? basketItem.price * basketItem.qnt : undefined,
    items: [
      {
        item_id: itemId,
        item_name: basketItem.assortName || "Produkt",
        price: basketItem.price,
        quantity: basketItem.qnt,
      },
    ],
  };
};

export const convertBasketToBeginCheckoutFormat = (
  basket: any[],
  total: number,
  promoCode?: string
): BeginCheckoutEvent => {
  const items: BeginCheckoutItem[] = basket.map((item) => {
    const itemCategory = (item.ticketAccess && item.ticketAccess[0]?.exerciseName) || getPolishItemCategory(item);
    let selectedDay: string | undefined;

    if (item.ticketAccess && item.ticketAccess[0]?.startDate) {
      try {
        const date = new Date(item.ticketAccess[0].startDate);
        selectedDay = format(date, "yyyy-MM-dd");
      } catch (error) {
        console.warn("Error formatting ticketAccess startDate:", error);
      }
    } else if (item.validFrom) {
      try {
        const date = new Date(item.validFrom);
        selectedDay = format(date, "yyyy-MM-dd");
      } catch (error) {
        console.warn("Error formatting validFrom date:", error);
      }
    }

    const itemName = item.assortName || undefined;

    return {
      item_id: item.assortIds || "",
      item_name: itemName,
      item_category: itemCategory,
      price: item.price || 0,
      quantity: item.qnt || 1,
      selected_day: selectedDay,
    };
  });

  return {
    currency: "PLN",
    value: total,
    coupon: promoCode || undefined,
    items,
  };
};

/**
 * Pomocnicza funkcja do konwersji danych potwierdzenia na format dla GTM
 * @param confirmationData - Dane potwierdzenia zakupu
 * @returns Dane w formacie GTM dla eventu purchase
 */
export const convertConfirmationDataToGtmFormat = (confirmationData: ConfirmationData): PurchaseEvent => {
  // Zabezpieczenie przed brakiem danych (wsteczna kompatybilność)
  const lines = confirmationData?.lines || [];
  const transactionId = confirmationData?.transactionId || "";
  const total = confirmationData?.total ? parseFloat(confirmationData.total) : 0;
  const discountCode = confirmationData?.discountCode || undefined;
  const discountValue = confirmationData?.discountValue || undefined;
  const payName = confirmationData?.payName || undefined;

  const items: PurchaseItem[] = lines.map((line: any) => {
    // Używamy polskich nazw kategorii również w evencie purchase
    const itemCategory =
      line?.exerciseName || line?.exerciseGroupName || getPolishItemCategory(line) || line?.assortName || undefined;

    return {
      item_id: line?.assortIds || "",
      item_name: line?.assortName || "Brak nazwy",
      item_category: itemCategory,
      price: line?.price ? parseFloat(line.price) : 0,
      quantity: line?.qnt ? parseInt(line.qnt) : 1,
      selected_day: line?.exercise_startTime || undefined,
    };
  });

  return {
    transaction_id: transactionId,
    value: total,
    currency: "PLN",
    coupon: discountCode,
    discount_coupon: discountValue,
    payment_type: payName,
    items,
  };
};

/**
 * Pomocnicza funkcja do tłumaczenia kategorii na polskie nazwy
 * @param item - Element koszyka
 * @returns Polska nazwa kategorii lub undefined
 */
const getPolishItemCategory = (item: any): string | undefined => {
  if (item.ticket === true) {
    return "bilety";
  } else if (item.pass === true) {
    return "karnety";
  } else if (item.service === true) {
    return "usługi";
  } else if (item.accountIncome === true) {
    return "doładowanie konta";
  }
  return undefined;
};
